import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, Shield, MapPin, Star, ArrowUpRight, Sparkles, Globe } from "lucide-react";
import { AuroraBlue, MagneticButton, EBLUE } from "../shared/kursusUi";
import { loadTrustpilotWidget } from "../../utils/thirdParty";

const LOGO_DARK = "https://customer-assets.emergentagent.com/job_75937e85-5d04-4ca2-9da1-90520b393fdf/artifacts/nfov5wjr_v_m%C3%B8rk.png";

// Footeren ligger på 52 offentlige sider. Indtoningen er en CSS-animation (`.votra-rise`),
// hvor opacity kun står i @keyframes. Scroll-udløst framer-motion skrev `opacity: 0` som
// inline style, og prerenderingen snapshottede footeren, før animationen var kørt.

const TrustBox = () => {
  const ref = useRef(null);
  useEffect(() => {
    let cancelled = false;
    // Widgetten hentes først her, i stedet for i <head> på hver sidevisning.
    loadTrustpilotWidget()
      .then(() => {
        if (cancelled || !window.Trustpilot || !ref.current) return;
        window.Trustpilot.loadFromElement(ref.current, true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return (
    <div
      ref={ref}
      className="trustpilot-widget"
      data-locale="da-DK"
      data-template-id="56278e9abfbbba0bdcd568bc"
      data-businessunit-id="6a39837f642265226696b27f0"
      data-style-height="52px"
      data-style-width="100%"
      data-token="781084d1-1934-4bcd-9599-6a580f434e07"
      data-testid="trustpilot-trustbox"
    >
      <a href="https://www.trustpilot.com/review/votra.dk" target="_blank" rel="noopener noreferrer">Trustpilot</a>
    </div>
  );
};

const FooterLink = ({ to, children, ...rest }) => (
  <li>
    <Link
      to={to}
      className="group inline-flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors duration-300 font-geo"
      {...rest}
    >
      <span className="h-px w-0 bg-gradient-to-r from-[#2E6BFF] to-[#A855F7] transition-all duration-300 group-hover:w-4" />
      <span>{children}</span>
    </Link>
  </li>
);

const ColHeading = ({ children }) => (
  <h4 className="font-tech text-[11px] font-medium uppercase tracking-[0.28em] text-slate-500 mb-5">
    {children}
  </h4>
);

const Footer = () => {
  return (
    <footer className="relative overflow-hidden bg-[#05060E] text-white" data-testid="site-footer">
      <AuroraBlue grid nodes />
      {/* top hairline gradient */}
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(46,107,255,.6), rgba(168,85,247,.6), transparent)" }} aria-hidden />

      <div className="relative z-10">
        {/* ===== CTA BAND ===== */}
        <div className="border-b border-white/10">
          <div className="container mx-auto px-4 md:px-6 py-14 md:py-16">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
              <div className="votra-rise max-w-2xl">
                <p className="font-tech text-[11px] font-medium uppercase tracking-[0.28em] mb-4" style={{ color: "#5B8Cff" }}>
                  Klar til at komme videre?
                </p>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium leading-[1.05]">
                  Tal med os om din <span className="fx-grad fx-grad-anim">næste uddannelse</span>
                </h2>
                <p className="font-geo text-slate-400 mt-4 text-base md:text-lg max-w-xl">
                  Ring, skriv eller book en tid, vi hjælper dig med at finde det rette vognmand-, taxa- eller chaufførkursus.
                </p>
              </div>

              <div className="votra-rise flex flex-col sm:flex-row gap-3 shrink-0" style={{ animationDelay: "80ms" }}>
                <MagneticButton
                  href="tel:+4555292828"
                  className="fx-heartbeat inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-white font-semibold whitespace-nowrap no-underline"
                  style={{ background: `linear-gradient(120deg, ${EBLUE}, #7C3AED)` }}
                  data-testid="footer-call-cta"
                >
                  <Phone className="h-5 w-5" />
                  Ring +45 55 29 28 28
                </MagneticButton>
                <Link
                  to="/kontakt"
                  className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold whitespace-nowrap border border-white/15 bg-white/[0.04] backdrop-blur-xl text-white hover:bg-white/[0.08] hover:border-white/30 transition-all duration-300"
                  data-testid="footer-contact-cta"
                >
                  <Sparkles className="h-4 w-4" style={{ color: "#00E5FF" }} />
                  Kontakt os
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* ===== TRUSTPILOT ===== */}
        <div className="border-b border-white/10" data-testid="trustpilot-section">
          <div className="container mx-auto px-4 md:px-6 py-7">
            <div className="flex flex-col md:flex-row items-center justify-between gap-5 text-center md:text-left">
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="flex items-center gap-2">
                  <Star className="h-6 w-6" style={{ color: '#00b67a', fill: '#00b67a' }} />
                  <span className="text-lg font-bold tracking-tight">Trustpilot</span>
                </div>
                <div>
                  <p className="font-semibold font-geo">Er du tilfreds med Votra?</p>
                  <p className="text-sm text-slate-400 font-geo">Del din oplevelse og hjælp andre med at vælge rigtigt.</p>
                </div>
              </div>
              <div className="flex items-center gap-3 w-full md:w-auto">
                <a
                  href="https://www.trustpilot.com/evaluate/votra.dk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-white transition-transform hover:scale-105 whitespace-nowrap"
                  style={{ backgroundColor: '#00b67a' }}
                  data-testid="trustpilot-review-btn"
                >
                  <Star className="h-4 w-4" fill="white" /> Skriv en anmeldelse
                </a>
                <div className="hidden md:block md:min-w-[260px]">
                  <TrustBox />
                </div>
              </div>
            </div>
            <div className="md:hidden mt-4">
              <TrustBox />
            </div>
          </div>
        </div>

        {/* ===== MAIN GRID ===== */}
        <div className="container mx-auto px-4 md:px-6 py-14">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
            {/* Contact / brand */}
            <div className="votra-rise">
              <div className="flex items-center gap-3 mb-6">
                <div className="rounded-2xl p-[1px]" style={{ background: "linear-gradient(135deg, rgba(46,107,255,.5), rgba(124,58,237,.5))" }}>
                  <div className="rounded-2xl bg-[#0A0B14] p-1.5">
                    <img src={LOGO_DARK} alt="Votra" className="h-11 w-auto" />
                  </div>
                </div>
                <div>
                  <span className="text-xl font-bold block font-display">Votra</span>
                  <span className="text-sm text-slate-400 font-geo">Vognmandsuddannelser</span>
                </div>
              </div>
              <div className="space-y-3 text-slate-300 font-geo text-sm">
                <a href="tel:+4555292828" className="flex items-center gap-2.5 hover:text-white transition-colors group">
                  <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-white/[0.05] border border-white/10 group-hover:border-[#2E6BFF]/50 transition-colors"><Phone className="h-4 w-4" style={{ color: "#5B8Cff" }} /></span>
                  +45 55 29 28 28
                </a>
                <a href="mailto:kundeservice@votra.dk" className="flex items-center gap-2.5 hover:text-white transition-colors group">
                  <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-white/[0.05] border border-white/10 group-hover:border-[#2E6BFF]/50 transition-colors"><Mail className="h-4 w-4" style={{ color: "#5B8Cff" }} /></span>
                  kundeservice@votra.dk
                </a>
                <a href="mailto:sikker@votra.dk" className="flex items-center gap-2.5 hover:text-white transition-colors group">
                  <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-white/[0.05] border border-white/10 group-hover:border-[#2E6BFF]/50 transition-colors"><Shield className="h-4 w-4" style={{ color: "#00E5FF" }} /></span>
                  sikker@votra.dk
                </a>
                <div className="flex items-center gap-2.5 pl-0.5">
                  <span className="text-slate-500 font-tech text-xs uppercase tracking-widest">CVR</span>
                  <span>36486899</span>
                </div>
                <div className="flex items-start gap-2.5 pt-1">
                  <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-white/[0.05] border border-white/10 shrink-0"><MapPin className="h-4 w-4" style={{ color: "#A855F7" }} /></span>
                  <div>
                    <p>Sct. Hansgade 9, 2. sal</p>
                    <p>4100 Ringsted</p>
                    <Link
                      to="/adresser"
                      className="inline-flex items-center gap-1 mt-1.5 text-sm text-slate-400 hover:text-white transition-colors group"
                      data-testid="footer-all-addresses-link"
                    >
                      Se alle adresser
                      <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="votra-rise" style={{ animationDelay: "80ms" }}>
              <ColHeading>Navigation</ColHeading>
              <ul className="space-y-3 text-sm">
                <FooterLink to="/">Kurser</FooterLink>
                <FooterLink to="/kalender">Kursuskalender</FooterLink>
                <FooterLink to="/aktive-hold-kalender" data-testid="footer-aktive-hold-link">Aktive hold kalender</FooterLink>
                <FooterLink to="/viden" data-testid="footer-viden-link">Viden</FooterLink>
                <FooterLink to="/lokationer" data-testid="footer-lokationer-link">Lokationer</FooterLink>
                <FooterLink to="/om-os">Om os</FooterLink>
                <FooterLink to="/minskole">Log ind</FooterLink>
              </ul>
            </div>

            {/* For users */}
            <div className="votra-rise" style={{ animationDelay: "160ms" }}>
              <ColHeading>For brugere</ColHeading>
              <ul className="space-y-3 text-sm">
                <FooterLink to="/ledige">Ledige</FooterLink>
                <FooterLink to="/jobcentre">Jobcentre &amp; A-kasser</FooterLink>
                <FooterLink to="/partner">Partnerprogram</FooterLink>
                <FooterLink to="/fvu" data-testid="footer-fvu-link">Dansk trin 2 (FVU)</FooterLink>
                <FooterLink to="/nyttige-links">Nyttige links</FooterLink>
                <li>
                  <a href="https://blog.votra.dk/blog?lang=da" data-testid="footer-blog-link"
                    className="group inline-flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors duration-300 font-geo">
                    <span className="h-px w-0 bg-gradient-to-r from-[#2E6BFF] to-[#A855F7] transition-all duration-300 group-hover:w-4" />
                    <span>Blog</span>
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div className="votra-rise" style={{ animationDelay: "240ms" }}>
              <ColHeading>Information</ColHeading>
              <ul className="space-y-3 text-sm">
                <FooterLink to="/kontakt">Kontakt os</FooterLink>
                <FooterLink to="/business" data-testid="footer-business-link">Votra Business, drift &amp; økonomi</FooterLink>
                <FooterLink to="/vognmand-onboarding" data-testid="footer-vognmand-onboarding-link">Vognmand Onboarding</FooterLink>
                <FooterLink to="/persondata">Persondata og cookies</FooterLink>
                <FooterLink to="/kursusbetingelser">Kursusbetingelser</FooterLink>
                <FooterLink to="/registrering" data-testid="footer-registrering-link">Registrering</FooterLink>
                <FooterLink to="/tilmeldingsblanket" data-testid="footer-tilmeldingsblanket-link">Tilmeldingsblanket</FooterLink>
              </ul>
            </div>
          </div>

          {/* Sprogvælger — sider på engelsk, arabisk og ukrainsk */}
          <div className="border-t border-white/10 mt-12 pt-8 flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="footer-languages">
            <span className="font-tech text-[11px] uppercase tracking-[0.24em] text-slate-500 flex items-center gap-2">
              <Globe className="h-3.5 w-3.5" style={{ color: "#5B8Cff" }} /> Sprog
            </span>
            <Link to="/en" lang="en" className="px-3 py-1.5 rounded-full border border-white/15 bg-white/[0.04] text-sm text-slate-300 hover:text-white hover:border-white/30 transition-colors" data-testid="footer-lang-en">English</Link>
            <Link to="/ar" lang="ar" dir="rtl" className="px-3 py-1.5 rounded-full border border-white/15 bg-white/[0.04] text-sm text-slate-300 hover:text-white hover:border-white/30 transition-colors" data-testid="footer-lang-ar">العربية</Link>
            <Link to="/uk" lang="uk" className="px-3 py-1.5 rounded-full border border-white/15 bg-white/[0.04] text-sm text-slate-300 hover:text-white hover:border-white/30 transition-colors" data-testid="footer-lang-uk">Українська</Link>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-white/10 mt-8 pt-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-slate-500 text-sm font-geo">
            <p>&copy; {new Date().getFullYear()} Votra Danmark ApS. Alle rettigheder forbeholdes.</p>
            <p className="font-tech text-[11px] uppercase tracking-[0.24em] text-slate-600">Fremtidens uddannelsescenter</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
